import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bike, Save, Plus, Trash2, CheckCircle2, Circle as CircleIcon, Navigation, Search, Store, Play, Home, Check, ClipboardList, PackageSearch, X, RotateCw,Truck } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap, Popup, Circle } from 'react-leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import L from 'leaflet';
import io from 'socket.io-client';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';

import 'leaflet/dist/leaflet.css';
import '../../styles/Entregas.css';

/* ================= ICONES PERSONALIZADOS ================= */
const createNumberedIcon = (number, color, type = 'moto') => {
    const emoji = type === 'bike' ? "🚲" : "🛵";
    const markup = renderToStaticMarkup(
        <div className="pulse-icon" style={{ 
            backgroundColor: color, color: 'white', width: '36px', height: '36px', 
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', 
            fontWeight: 'bold', border: '2px solid white', boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
        }}>
            {number || emoji}
        </div>
    );
    return L.divIcon({ html: markup, className: 'custom-number-icon', iconSize: [36, 36], iconAnchor: [18, 18] });
};

const createStoreIcon = (logoUrl, aberto) => {
    const finalLogo = logoUrl ? logoUrl : 'https://via.placeholder.com/50';
    const markup = renderToStaticMarkup(
        <div style={{ position: 'relative', width: '50px', height: '50px' }}>
            <div style={{
                width: '50px', height: '50px', borderRadius: '50%', overflow: 'hidden',
                border: '3px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <img src={finalLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{
                position: 'absolute', bottom: '2px', right: '2px', width: '14px', height: '14px',
                borderRadius: '50%', background: aberto ? '#10b981' : '#ef4444', border: '2px solid white', zIndex: 10
            }} />
        </div>
    );
    return L.divIcon({ html: markup, className: 'custom-store-icon', iconSize: [50, 50], iconAnchor: [25, 25], popupAnchor: [0, -25] });
};

const clientIcon = (num) => createNumberedIcon(num, '#ea1d2c');

const ChangeView = ({ center }) => {
    const map = useMap();
    useEffect(() => { if (center) map.setView(center, 14); }, [center, map]);
    return null;
};

const Entregas = ({ estabelecimentoId }) => {
    const [metodoAtivo, setMetodoAtivo] = useState('raio');
    const [taxasRaio, setTaxasRaio] = useState([]);
    const [taxasBairro, setTaxasBairro] = useState([]);
    const [novoRaio, setNovoRaio] = useState({ ate_km: '', valor: '' });
    const [novoBairro, setNovoBairro] = useState({ nome: '', valor: '' });
    const [dadosLoja, setDadosLoja] = useState(null);
    const [posicaoLoja, setPosicaoLoja] = useState(null);
    const [rotaSequencial, setRotaSequencial] = useState([]); 
    const [entregadoresNoMapa, setEntregadoresNoMapa] = useState({});
    const [simulando, setSimulando] = useState(false);
    const [atualizando, setAtualizando] = useState(false); // Estado para animação do refresh
    const [pedidoBusca, setPedidoBusca] = useState('');
    const [resumoSimulacao, setResumoSimulacao] = useState(null);
    const [raioPreview, setRaioPreview] = useState(null);
    const [solicitandoIFood, setSolicitandoIFood] = useState(false);
    const socket = useRef(null);
    const coresRaio = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

    useEffect(() => {
        socket.current = io('https://app.universomidiadigital.com.br', { transports: ['websocket'], secure: true });
        socket.current.emit('join-loja', estabelecimentoId);
        socket.current.on('position-changed', (data) => {
            setEntregadoresNoMapa(prev => ({ ...prev, [data.entregadorId]: data }));
        });
        return () => socket.current?.disconnect();
    }, [estabelecimentoId]);

    const carregarDados = useCallback(async (isRefresh = false) => {
        if (isRefresh) setAtualizando(true);
        try {
            const [config, res] = await Promise.all([
                api.get(`/cardapio/estabelecimentos/${estabelecimentoId}/config-entrega-completa`),
                api.get(`/cardapio/estabelecimento/${estabelecimentoId}`)
            ]);
            if (config.data) {
                setMetodoAtivo(config.data.metodo || 'raio');
                setTaxasRaio(config.data.raio || []);
                setTaxasBairro(config.data.bairro || []);
            }
            if (res.data && res.data.lat) {
                setDadosLoja(res.data);
                setPosicaoLoja([parseFloat(res.data.lat), parseFloat(res.data.lng)]);
            }
            if (isRefresh) toast.success("Dados atualizados!");
        } catch (e) { 
            toast.error("Erro ao atualizar dados"); 
        } finally {
            if (isRefresh) setAtualizando(false);
        }
    }, [estabelecimentoId]);

    useEffect(() => { carregarDados(); }, [carregarDados]);

    const verificarLojaAberta = (abertura, fechamento) => {
        if (!abertura || !fechamento) return false;
        const agora = new Date();
        const [horaA, minA] = abertura.split(':').map(Number);
        const [horaF, minF] = fechamento.split(':').map(Number);
        const inicio = new Date(); inicio.setHours(horaA, minA, 0);
        const fim = new Date(); fim.setHours(horaF, minF, 0);
        if (fim < inicio) return agora >= inicio || agora <= fim;
        return agora >= inicio && agora <= fim;
    };
    
    const lojaAberta = verificarLojaAberta(dadosLoja?.horario_abertura, dadosLoja?.horario_fechamento);

    const adicionarPontoNaRota = async (latlng, dadosPedido = null) => {
        if (!posicaoLoja) return;
        const origem = rotaSequencial.length === 0 
            ? { lat: posicaoLoja[0], lng: posicaoLoja[1] } 
            : { lat: rotaSequencial[rotaSequencial.length-1].destino[0], lng: rotaSequencial[rotaSequencial.length-1].destino[1] };
        
        toast.loading("Calculando trecho...", { id: "rota-seq" });
        try {
            const resp = await fetch(`https://router.project-osrm.org/route/v1/driving/${origem.lng},${origem.lat};${latlng.lng},${latlng.lat}?overview=full&geometries=geojson`);
            const data = await resp.json();
            if (data.routes?.length > 0) {
                const dist = (data.routes[0].distance / 1000).toFixed(2);
                const novo = { 
                    id: Date.now(), 
                    pedido: dadosPedido || { cliente_nome: `Ponto ${rotaSequencial.length + 1}`, id: 'Manual' }, 
                    destino: [latlng.lat, latlng.lng], 
                    caminho: data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]), 
                    distancia: dist 
                };
                setRotaSequencial(prev => [...prev, novo]);
                toast.success("Parada adicionada!", { id: "rota-seq" });
            }
        } catch (e) { toast.error("Erro na rota"); }
    };

    const buscarPedidoReal = async () => {
        if (!pedidoBusca) return;
        try {
            const res = await api.get(`/pedidos/detalhes/${pedidoBusca}`);
            if (res.data.success && res.data.pedido.lat_cliente) {
                const p = res.data.pedido;
                adicionarPontoNaRota({ lat: parseFloat(p.lat_cliente), lng: parseFloat(p.lng_cliente) }, p);
                setPedidoBusca('');
            } else { toast.error("Pedido sem GPS"); }
        } catch (e) { toast.error("Pedido não encontrado"); }
    };
    
    const solicitarEntregaExterna = async (pedidoId) => {
    setSolicitandoIFood(true);
    toast.loading("Comunicando com iFood...", { id: 'ifood-req' });
    
    try {
        // Chamamos a rota de despacho passando a flag 'usar_ifood'
        const res = await api.put(`/pedidos/despachar/${pedidoId}`, {
            usar_ifood: true,
            entregador_nome: 'Logística iFood'
        });

        if (res.data.success) {
            toast.success("Entrega iFood solicitada com sucesso!", { id: 'ifood-req' });
            carregarDados(true); // Atualiza a lista/mapa
        } else {
            throw new Error();
        }
    } catch (e) {
        toast.error("Erro ao solicitar iFood. Verifique suas credenciais.", { id: 'ifood-req' });
    } finally {
        setSolicitandoIFood(false);
    }
};

const calcularTaxaDinamica = (distanciaKm) => {
    if (metodoAtivo === 'raio') {
        // Busca a primeira faixa de KM que seja maior ou igual à distância do pedido
        const faixa = taxasRaio.find(t => parseFloat(t.ate_km) >= parseFloat(distanciaKm));
        return faixa ? parseFloat(faixa.valor) : 0;
    }
    return 0; // Para outros métodos, você pode expandir a lógica
};

// Calcula o valor da entrega baseado na distância e nas configurações de raio
const calcularTaxaPorDistancia = (distanciaKm) => {
    if (!distanciaKm || !taxasRaio || taxasRaio.length === 0) return 0;

    // Removido o espaço entre "faixas" e "Ordenadas"
    const faixasOrdenadas = [...taxasRaio].sort((a, b) => parseFloat(a.ate_km) - parseFloat(b.ate_km));
    
    const faixa = faixasOrdenadas.find(t => parseFloat(distanciaKm) <= parseFloat(t.ate_km));

    return faixa ? parseFloat(faixa.valor) : 0;
};


    const iniciarSimulacao = () => {
        if (rotaSequencial.length === 0) return toast.error("Adicione paradas");
        setSimulando(true);
        const percursoTotal = rotaSequencial.reduce((acc, r) => [...acc, ...r.caminho], []);
        let i = 0;
        const motoId = `MOTO-SIM-${estabelecimentoId}`;
        const interval = setInterval(() => {
            if (i >= percursoTotal.length) {
                clearInterval(interval);
                setSimulando(false);
                setResumoSimulacao([...rotaSequencial]);
                return;
            }
            const [lat, lng] = percursoTotal[i];
            const distTotal = rotaSequencial.reduce((acc, r) => acc + parseFloat(r.distancia), 0);
            const veiculo = distTotal <= 10 ? 'bike' : 'moto';
            const data = { estabelecimentoId, entregadorId: motoId, lat, lng, veiculo, nome: "Simulador" };
            if (socket.current) socket.current.emit('update-location', data);
            setEntregadoresNoMapa(prev => ({ ...prev, [motoId]: data }));
            i += Math.ceil(percursoTotal.length / 100);
        }, 100);
    };

    const limparMapa = () => {
        setRotaSequencial([]);
        setEntregadoresNoMapa({}); 
        setResumoSimulacao(null);
        toast.success("Mapa limpo");
    };

    const MapEvents = () => {
        useMapEvents({
            click: (e) => {
                if (metodoAtivo === 'mapa') adicionarPontoNaRota(e.latlng);
                if (metodoAtivo === 'raio') {
                    const dist = (L.latLng(posicaoLoja).distanceTo(e.latlng) / 1000).toFixed(1);
                    setRaioPreview(dist);
                    setNovoRaio({ ate_km: dist, valor: '' });
                }
            }
        });
        return null;
    };


const despacharPedidoLogistica = async (parada) => {
    // Evita duplicar despacho de pontos manuais sem pedido vinculado
    if (!parada.pedido || parada.pedido.id === 'Manual') {
        toast.error("Este ponto não possui um pedido vinculado para despacho.");
        return;
    }

    setAtualizando(true);
    const toastId = toast.loading(`Despachando pedido #${parada.pedido.id}...`);
    
    try {
        // 1. Cálculo do valor real baseado na distância retornada pelo OSRM
        const valorFinal = calcularTaxaPorDistancia(parada.distancia);

        // 2. Chamada para o seu novo endpoint robusto
        const res = await api.put(`/pedidos/despachar/${parada.pedido.id}`, {
            entregador_id: null, // Pode ser expandido para um Select de motoboys
            entregador_nome: metodoAtivo === 'ifood' ? 'Logística iFood' : 'Entregador Próprio',
            usar_ifood: metodoAtivo === 'ifood',
            valor_entrega: valorFinal,
            distancia_km: parada.distancia,
            caminho_polyline: parada.caminho // Enviando as coordenadas para o histórico do banco
        });

        if (res.data.success) {
            toast.success(`Pedido #${parada.pedido.id} em trânsito!`, { id: toastId });

            // 3. Atualiza o estado visual local para refletir os valores salvos
            setRotaSequencial(prev => prev.map(item => 
                item.id === parada.id 
                ? { ...item, status: 'despachado', valor_real: valorFinal } 
                : item
            ));

            // 4. Se for o primeiro despacho e não for iFood, inicia o deslocamento no mapa
            if (!simulando && metodoAtivo !== 'ifood') {
                iniciarSimulacao();
            }
            
            return true;
        }
    } catch (err) {
        console.error("Erro no Despacho:", err);
        toast.error(`Falha ao despachar #${parada.pedido.id}`, { id: toastId });
        return false;
    } finally {
        setAtualizando(false);
    }
};

    const salvarConfig = async () => {
        try {
            await api.post(`/cardapio/estabelecimentos/${estabelecimentoId}/config-entrega-completa`, { 
                metodo: metodoAtivo, raio: taxasRaio, bairro: taxasBairro 
            });
            toast.success("Salvo!");
        } catch (e) { toast.error("Erro ao salvar"); }
    };

return (
    <div className="entregas-wrapper">
        <Toaster position="top-right" />
   

        {/* Modal de Resumo da Rota */}
        {resumoSimulacao && (
            <div className="modal-overlay">
                <div className="modal-content fade-in">
                    <CheckCircle2 color="#10b981" size={48} style={{ margin: '0 auto 15px' }} />
                    <h2>Rota Finalizada!</h2>
                    <div className="resumo-lista" style={{ textAlign: 'left', maxHeight: '200px', overflowY: 'auto' }}>
                        {resumoSimulacao.map((r, idx) => (
                            <div key={idx} className="resumo-item" style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                                <strong>{idx + 1}ª: {r.pedido.cliente_nome}</strong><br />
                                <small>Trecho: {r.distancia} KM</small>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button className="btn-save-main" style={{ flex: 1 }} onClick={limparMapa}>Limpar Tudo</button>
                        <button className="btn-modal-close" style={{ flex: 1, marginTop: 0 }} onClick={() => setResumoSimulacao(null)}>Fechar</button>
                    </div>
                </div>
            </div>
        )}

        {/* Header da Página */}
        <div className="entregas-header">
            <div>
                <h2><Bike color="#ea1d2c" size={30} /> Logística Inteligente</h2>
                <p className="loja-label"><Store size={14} /> {dadosLoja?.nome || 'Unidade'}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
                <button
                    className="btn-add"
                    style={{ background: '#64748b' }}
                    onClick={() => carregarDados(true)}
                    disabled={atualizando}
                >
                    <RotateCw size={18} className={atualizando ? 'pulse' : ''} />
                    {atualizando ? 'Atualizando...' : 'Atualizar'}
                </button>

                <button className="btn-save-global" onClick={salvarConfig}>
                    <Save size={18} /> Salvar Configurações
                </button>
            </div>
        </div>

        {/* Grid de Seleção de Métodos */}
        <div className="metodo-grid">
            <div className={`metodo-card ${metodoAtivo === 'raio' ? 'active' : ''}`} onClick={() => setMetodoAtivo('raio')}>
                {metodoAtivo === 'raio' ? <CheckCircle2 className="icon-check" /> : <CircleIcon className="icon-uncheck" />}
                <div className="metodo-info">
                    <strong>Zonas de Raio</strong>
                    <span>Configurar KM linear</span>
                </div>
            </div>

            <div className={`metodo-card ${metodoAtivo === 'ifood' ? 'active' : ''}`} onClick={() => setMetodoAtivo('ifood')}>
                {metodoAtivo === 'ifood' ? <CheckCircle2 className="icon-check" /> : <CircleIcon className="icon-uncheck" />}
                <div className="metodo-info">
                    <strong>iFood On-Demand</strong>
                    <span>Logística terceirizada</span>
                </div>
            </div>

            <div className={`metodo-card ${metodoAtivo === 'bairro' ? 'active' : ''}`} onClick={() => setMetodoAtivo('bairro')}>
                {metodoAtivo === 'bairro' ? <CheckCircle2 className="icon-check" /> : <CircleIcon className="icon-uncheck" />}
                <div className="metodo-info">
                    <strong>Taxa por Bairro</strong>
                    <span>Preços por região</span>
                </div>
            </div>

            <div className={`metodo-card ${metodoAtivo === 'mapa' ? 'active' : ''}`} onClick={() => setMetodoAtivo('mapa')}>
                {metodoAtivo === 'mapa' ? <CheckCircle2 className="icon-check" /> : <CircleIcon className="icon-uncheck" />}
                <div className="metodo-info">
                    <strong>Mapa & Simulação</strong>
                    <span>Rota de múltiplas paradas</span>
                </div>
            </div>
        </div>

        {/* Conteúdo Dinâmico */}
        <div className="entregas-content fade-in">
            <div style={{ display: 'flex', gap: '20px', flexDirection: window.innerWidth < 900 ? 'column' : 'row' }}>
                
                {/* Coluna de Configurações (Esquerda) */}
                <div style={{ flex: 1 }}>
                    {metodoAtivo === 'ifood' && (
                        <div className="fade-in">
                            <h3>Solicitar Motoboy iFood</h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                                Use este módulo para pedidos que necessitam de entrega externa.
                            </p>
                            <div className="search-box">
                                <PackageSearch size={18} color="#64748b" />
                                <input
                                    type="text"
                                    placeholder="ID do Pedido para iFood"
                                    value={pedidoBusca}
                                    onChange={e => setPedidoBusca(e.target.value)}
                                />
                                <button
                                    onClick={() => solicitarEntregaExterna(pedidoBusca)}
                                    disabled={solicitandoIFood || !pedidoBusca}
                                    style={{ background: '#ea1d2c' }}
                                >
                                    {solicitandoIFood ? <RotateCw className="pulse" size={16} /> : 'Solicitar Agora'}
                                </button>
                            </div>
                            <div style={{ marginTop: '20px', padding: '15px', background: '#fff4f4', borderRadius: '8px', border: '1px solid #ffe2e2' }}>
                                <small style={{ color: '#ea1d2c', fontWeight: 'bold' }}>Atenção:</small><br />
                                <small style={{ color: '#666' }}>
                                    Certifique-se de que o pedido já está pago. As taxas são calculadas pelo iFood.
                                </small>
                            </div>
                        </div>
                    )}

                    {metodoAtivo === 'raio' && (
                        <div className="fade-in">
                            <h3>Definir Taxas por KM</h3>
                            <div className="form-inline">
                                <input type="number" placeholder="Até KM" value={novoRaio.ate_km} onChange={e => { setNovoRaio({ ...novoRaio, ate_km: e.target.value }); setRaioPreview(e.target.value) }} />
                                <input type="number" placeholder="Valor R$" value={novoRaio.valor} onChange={e => setNovoRaio({ ...novoRaio, valor: e.target.value })} />
                                <button className="btn-add" onClick={() => {
                                    setTaxasRaio([...taxasRaio, { ...novoRaio, id: Date.now() }].sort((a, b) => a.ate_km - b.ate_km));
                                    setNovoRaio({ ate_km: '', valor: '' }); setRaioPreview(null);
                                }}><Plus size={18} /></button>
                            </div>
                            <table className="taxas-table">
                                <thead><tr><th>Distância</th><th>Valor</th><th>Ação</th></tr></thead>
                                <tbody>
                                    {taxasRaio.map(t => (
                                        <tr key={t.id}>
                                            <td>Até {t.ate_km} KM</td>
                                            <td>R$ {t.valor}</td>
                                            <td><button className="btn-del" onClick={() => setTaxasRaio(taxasRaio.filter(x => x.id !== t.id))}><Trash2 size={16} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {metodoAtivo === 'bairro' && (
                        <div className="fade-in">
                            <h3>Taxas por Bairro</h3>
                            <div className="form-inline">
                                <input type="text" placeholder="Nome do Bairro" value={novoBairro.nome} onChange={e => setNovoBairro({ ...novoBairro, nome: e.target.value })} />
                                <input type="number" placeholder="Valor R$" value={novoBairro.valor} onChange={e => setNovoBairro({ ...novoBairro, valor: e.target.value })} />
                                <button className="btn-add" onClick={() => {
                                    setTaxasBairro([...taxasBairro, { ...novoBairro, id: Date.now() }]);
                                    setNovoBairro({ nome: '', valor: '' });
                                }}><Plus size={18} /></button>
                            </div>
                            <table className="taxas-table">
                                <thead><tr><th>Bairro</th><th>Valor</th><th>Ação</th></tr></thead>
                                <tbody>
                                    {taxasBairro.map(t => (
                                        <tr key={t.id}>
                                            <td>{t.nome}</td>
                                            <td>R$ {t.valor}</td>
                                            <td><button className="btn-del" onClick={() => setTaxasBairro(taxasBairro.filter(x => x.id !== t.id))}><Trash2 size={16} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {metodoAtivo === 'mapa' && (
                        <div className="fade-in">
                            <div className="map-header">
                                <div className="search-box">
                                    <PackageSearch size={18} color="#64748b" />
                                    <input type="text" placeholder="Vincular Pedido (ID)" value={pedidoBusca} onChange={e => setPedidoBusca(e.target.value)} onKeyPress={e => e.key === 'Enter' && buscarPedidoReal()} />
                                    <button onClick={buscarPedidoReal}>Vincular</button>
                                </div>
                            </div>
                                 {/* Adicione isso dentro do map-wrapper ou acima dele */}
                            <div className="logistica-stats">
                            <div className="stat-card">
                                <label>Total em Rotas</label>
                               <strong>
                                R$ {(Array.isArray(rotaSequencial) ? rotaSequencial : []).reduce((acc, curr) => {
                                    try {
                                        // 1. Tenta pegar o valor do pedido
                                        const valorPedido = parseFloat(curr?.pedido?.valor_entrega);
                                        
                                        // 2. Se não for número, tenta calcular pela distância
                                        if (isNaN(valorPedido)) {
                                            const valorCalculado = parseFloat(calcularTaxaPorDistancia(curr?.distancia));
                                            return acc + (isNaN(valorCalculado) ? 0 : valorCalculado);
                                        }
                            
                                        return acc + valorPedido;
                                    } catch (e) {
                                        // Se qualquer coisa falhar na iteração, retorna o acumulado atual para não quebrar
                                        console.error("Erro no cálculo de item da rota:", e);
                                        return acc;
                                    }
                                }, 0).toFixed(2)}
                            </strong>
                            </div>
                            <div className="stat-card">
                                <label>Pedidos em Trânsito</label>
                                <strong>{rotaSequencial.length}</strong>
                            </div>
                        </div>
                                                    
                            <div className="rota-preview" style={{ margin: '15px 0', maxHeight: '200px', overflowY: 'auto' }}>
                                {rotaSequencial.map((p, i) => (
                                    <div key={p.id} className="resumo-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8fafc', marginBottom: '5px', borderRadius: '8px', borderLeft: '4px solid #ea1d2c' }}>
                                        <span><strong>{i + 1}º</strong> - {p.pedido.cliente_nome}</span>
                                        <button onClick={() => setRotaSequencial(rotaSequencial.filter(x => x.id !== p.id))} style={{ border: 'none', background: 'transparent', color: '#ef4444' }}><X size={16} /></button>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                               <button 
                                className="btn-simulate" 
                                style={{ flex: 1, background: '#10b981', fontWeight: 'bold' }} 
                                onClick={async () => {
                                    // Despacha todos os pedidos da lista antes de mover a moto
                                    for (const parada of rotaSequencial) {
                                        await despacharPedidoLogistica(parada);
                                    }
                                    // Inicia a movimentação visual no mapa
                                    iniciarSimulacao();
                                }} 
                                disabled={simulando || rotaSequencial.length === 0}
                            >
                                <Truck size={18} /> DESPACHAR E INICIAR MONITORAMENTO
                            </button>
                                <button className="btn-del" onClick={limparMapa}>Limpar</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Coluna do Mapa (Direita) */}
                <div style={{ flex: 2 }}>
                    <div className="map-wrapper" style={{ height: '450px', position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        {posicaoLoja && (
                            <MapContainer center={posicaoLoja} zoom={14} style={{ height: '100%', width: '100%' }}>
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <ChangeView center={posicaoLoja} />

                                <Marker position={posicaoLoja} icon={createStoreIcon(dadosLoja?.logo_url, lojaAberta)}>
                                    <Popup>
                                        <div style={{ minWidth: '200px' }}>
                                            <h3 style={{ color: '#ea1d2c' }}>{dadosLoja?.nome}</h3>
                                            <p>{dadosLoja?.endereco}</p>
                                        </div>
                                    </Popup>
                                </Marker>

                                {metodoAtivo === 'raio' && taxasRaio.map((t, idx) => (
                                    <Circle key={t.id} center={posicaoLoja} radius={parseFloat(t.ate_km) * 1000} pathOptions={{ color: coresRaio[idx % coresRaio.length], fillOpacity: 0.1 }} />
                                ))}

                                {rotaSequencial.map((r, i) => (
                                    <React.Fragment key={r.id}>
                                        <Marker position={r.destino} icon={clientIcon(i + 1)}><Popup>{r.pedido.cliente_nome}</Popup></Marker>
                                        <Polyline positions={r.caminho} color="#3b82f6" weight={4} opacity={0.6} />
                                    </React.Fragment>
                                ))}

                                {(simulando || Object.keys(entregadoresNoMapa).length > 0) && Object.values(entregadoresNoMapa).map(m => (
                                    <Marker key={m.entregadorId} position={[m.lat, m.lng]} icon={createNumberedIcon(null, '#10b981', m.veiculo)}>
                                        <Popup>{m.nome}</Popup>
                                    </Marker>
                                ))}
                                <MapEvents />
                            </MapContainer>
                        )}
                    </div>
                </div>

            </div>
        </div>
    </div>
);
};

export default Entregas;